/**
 * Every word the Units settings form says (components/settings/UnitsSettings): what the section is for, what each system
 * shows, the sample values of its preview, and the server's refusals in plain words. Pure.
 */
import { ApiClientError } from "@/lib/api-client";
import { errorCodeOf, errorDetailOf } from "@/lib/api/error";

export const UNITS_TITLE = "Units";

export const UNITS_LEAD =
  "The units this plant reads its numbers in. Only what is shown changes: every value stays stored as it was recorded, with its own unit.";

export const READ_ONLY_NOTE = "Only an org admin can change the units. This is what the plant reads.";

export const READING_NOTE = "Reading the plant's units.";

/** Off the live platform there is no choice to read or save: the note stands in place of the form. */
export const LIVE_ONLY_NOTE = "The plant's units are set on the live platform. Sign in to the live platform to read and change them.";

export const READ_FAILED_NOTE = "The plant's units could not be read, so values show in metric for now. Reload the page to try again.";

export const FOLLOW_SYSTEM = "Follow the system";

/** A dimension the chosen system has no unit of (time; volume under US): every value is shown as recorded. */
export const AS_RECORDED = "As recorded";

/**
 * What each system shows, in a line. A system converts only the other system's units: ingest keeps bar, MPa and min
 * as the instrument reads them, so metric shows metric values as recorded.
 */
export const SYSTEM_LINES = {
  si: "US units in °C, kPa, kg, mm, L and L/min; metric values as recorded",
  us: "Metric units in °F, psi, lb, in and gal/min; time and volume as recorded",
} as const;

/** The other system's units, as the "follow the system" option names them. */
export const OTHER_SYSTEM_UNITS = { si: "US units", us: "metric units" } as const;

export const UNKNOWN_UNITS_NOTE = "A value in a unit this platform does not know (rpm, a percentage) is always shown as it was recorded.";

/** The four values the preview converts, as stored: one of temperature, pressure, mass and length. */
export const PREVIEW_SAMPLES: readonly { label: string; value: number; unit: string }[] = [
  { label: "Furnace temperature", value: 1450, unit: "°C" },
  { label: "Line pressure", value: 6.5, unit: "bar" },
  { label: "Ladle charge", value: 480, unit: "kg" },
  { label: "Riser height", value: 50.8, unit: "mm" },
];

/** A refusal or failure of the save, in words a person can act on. */
export function unitsErrorWords(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 403) return "Only an org admin can change the units.";
  const code = errorCodeOf(err);
  if (code === "invalid_units") return errorDetailOf(err) ?? "That choice of units is not one the platform knows. Pick again.";
  if (code === "invalid_body") return "That choice could not be read. Reload the page and try again.";
  return "The units could not be saved. Try again.";
}
